import React from "react";
import Container from "@/components/marketing/Container";
import { Briefcase, Unlock, Layers } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const DEFAULT_POINTS = [
  { icon: Briefcase, title: "No retainer", desc: "Pay per campaign, not per month." },
  { icon: Unlock, title: "No lock-in", desc: "You control when you go." },
  { icon: Layers, title: "One platform", desc: "Earned, guaranteed, and newswire together." },
];

/**
 * Positioning section. Centered content (eyebrow, headline, body, three
 * supporting points). When dark=true, renders on INK #0a0812 with lilac
 * icon accents. When dark=false (default), renders on LIGHT canvas.
 */
export default function PositioningSection({
  eyebrow = "WHY DIGITAL24",
  headline = "Expert earned coverage, without the agency retainer.",
  body = "The same expert outreach a PR agency gives you, per campaign, no retainer, no lock-in, and it extends across every channel.",
  points = DEFAULT_POINTS,
  dark = false,
}) {
  return (
    <section className={`relative overflow-hidden ${dark ? "bg-showcase" : "bg-canvas"}`}>
      {dark && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(55% 80% at 50% 0%, rgba(114,0,203,0.06) 0%, transparent 70%)" }}
        />
      )}

      <Container className="relative pt-7 sm:pt-9 lg:pt-12 pb-7 sm:pb-9 lg:pb-12 text-center">
        <Reveal>
          <div className="max-w-5xl mx-auto">
            <span className={`font-mono text-xs uppercase tracking-widest px-2 py-1 border inline-block mb-5 ${dark ? "border-[#3a3352] text-[#8a86a8]" : "border-border text-steel"}`}>
              {eyebrow}
            </span>
            <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5 ${dark ? "text-white" : "text-bone"}`}>
              {headline}
            </h2>
            <p className={`text-base lg:text-lg leading-relaxed mb-10 max-w-3xl mx-auto ${dark ? "text-mist" : "text-prose"}`}>
              {body}
            </p>
          </div>
        </Reveal>

        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-center gap-6 sm:gap-10 lg:gap-14 border-t pt-8 max-w-5xl mx-auto ${dark ? "border-[#3a3352]" : "border-[#e8e6ef]"}`}>
          {points.map((point, index) => (
            <Reveal variant="card" delay={index * 0.08} key={point.title}>
              <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:gap-2">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${dark ? "bg-cobalt/15" : "bg-cobalt/10"}`}>
                  <point.icon className={`w-5 h-5 ${dark ? "text-lilac" : "text-cobalt"}`} strokeWidth={1.75} />
                </div>
                <div className="sm:text-center">
                  <div className={`font-heading text-sm font-bold ${dark ? "text-white" : "text-bone"}`}>{point.title}</div>
                  <div className={`text-xs ${dark ? "text-mist" : "text-steel"}`}>{point.desc}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}